import { ExternalLinkIcon } from 'lucide-react'
import {
  buildEurLexHtmlUrlFromCelex,
  isAnnexCitationLabel,
  resolveEurLexCelexForLinks,
  toLegislationCitationTarget,
  type LegislationCitationTarget,
  parseSearchRadarRagChunksOutput,
  regulationSnippetPreviewForUi,
} from './llmChatToolOutput'

export type LlmChatToolRegulationSourcesProps = {
  output: unknown
  viewOnEurLexLabel: string
  openInViewerLabel: string
  emptyLabel: string
  onOpenCitation: (target: LegislationCitationTarget) => void
}

function isAnnexLikeSourcePreview(input: string | null): boolean {
  if (!input) {
    return false
  }
  const trimmed = input.trim()
  if (trimmed.length === 0) {
    return false
  }
  return isAnnexCitationLabel(trimmed)
}

export function LlmChatToolRegulationSources({
  output,
  viewOnEurLexLabel,
  openInViewerLabel,
  emptyLabel,
  onOpenCitation,
}: LlmChatToolRegulationSourcesProps) {
  const parsed = parseSearchRadarRagChunksOutput(output)
  if (!parsed) {
    return null
  }

  if (parsed.items.length === 0) {
    return (
      <p className='border-t border-border/40 px-3 py-2 text-[12px] text-muted-foreground'>
        {emptyLabel}
      </p>
    )
  }

  return (
    <div className='border-t border-border/40 px-3 py-2'>
      <div className='space-y-2'>
        {parsed.items.map((item, itemIndex) => {
          const eurLexUrl = buildEurLexHtmlUrlFromCelex(
            resolveEurLexCelexForLinks(item)
          )
          const citationTarget = toLegislationCitationTarget(item)
          const isAnnexCitation =
            isAnnexLikeSourcePreview(item.evidencePreview) ||
            isAnnexLikeSourcePreview(item.chunkText)
          let viewerCitationTarget = citationTarget
          if (isAnnexCitation) {
            viewerCitationTarget = null
          }
          const regulationLabel =
            item.instrumentTitle !== null
              ? item.instrumentTitle
              : `CELEX ${item.celex}`
          const previewText = regulationSnippetPreviewForUi(item)
          return (
            <article
              key={`${item.celex}-${itemIndex}`}
              className='rounded-md border border-border bg-background p-2'
            >
              <header className='mb-1.5 flex flex-wrap items-start justify-between gap-x-2 gap-y-1'>
                <div className='min-w-0 flex-1'>
                  <div className='line-clamp-2 text-[12px] leading-snug font-semibold text-foreground'>
                    {regulationLabel}
                  </div>
                  {item.instrumentTitle !== null ? (
                    <div className='mt-0.5 text-[10px] text-muted-foreground'>
                      CELEX {item.celex}
                    </div>
                  ) : null}
                </div>
                {viewerCitationTarget || eurLexUrl ? (
                  <div className='flex shrink-0 items-center gap-1.5'>
                    {viewerCitationTarget ? (
                      <button
                        type='button'
                        className='inline-flex shrink-0 items-center gap-1 text-xs underline-offset-2 hover:underline'
                        onClick={() => {
                          onOpenCitation(viewerCitationTarget)
                        }}
                      >
                        {openInViewerLabel}
                      </button>
                    ) : null}
                    {eurLexUrl ? (
                      <a
                        href={eurLexUrl}
                        target='_blank'
                        rel='noreferrer noopener'
                        className='inline-flex shrink-0 items-center gap-1 text-xs underline-offset-2 hover:underline'
                      >
                        {viewOnEurLexLabel}
                        <ExternalLinkIcon className='size-3' />
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </header>
              <p className='line-clamp-4 text-xs leading-relaxed text-muted-foreground'>
                {previewText}
              </p>
            </article>
          )
        })}
      </div>
    </div>
  )
}
